import { FormHandles } from '@unform/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as yup from 'yup';

import { InputFileMedia } from '../../../../components/InputFileMedia';
import { InputText } from '../../../../components/InputText';
import { InputCheckbox } from '../../../../components/InputCheckbox';
import { Select } from '../../../../components/Select';
import { Painel } from '../../../../components/Painel';
import { FeatureGate } from '../../../../components/FeatureGate';
import {
  ComingSoonTag,
  DisabledField,
} from '../../../../components/ComingSoonTag';
import { useAuth } from '../../../../hooks/auth';
import { useToast } from '../../../../hooks/toast';
import { api } from '../../../../services/api';
import { getValidationErrors } from '../../../../utils/getValidationErrors';
import { cepMask, cnpjMask, phoneMask } from '../../../../utils/inputMasks';
import { isFeatureGranted } from '../../../../utils/isFeatureGranted';

import {
  Card,
  Confirm,
  Container,
  Field,
  FieldInput,
  Form,
  FullWidthCard,
  Grid,
  Info,
  PageTitle,
  Row2,
  Row4,
  Row5,
  ScheduleRow,
  Separate,
  ToggleRow,
  ToggleTrack,
} from './styles';

const UF_LIST =
  'AC,AL,AP,AM,BA,CE,DF,ES,GO,MA,MT,MS,MG,PA,PB,PR,PE,PI,RJ,RN,RS,RO,RR,SC,SP,SE,TO';

const UF_OPTIONS = UF_LIST.split(',').map(uf => ({ value: uf, label: uf }));

interface FormProps {
  name: string;
  phone?: string;
  address?: string;
  cnpj?: string;
  instagram?: string;
  number?: string;
  zipcode?: string;
  city?: string;
  uf?: string;
  capacity?: string;
  hours_weekdays_start?: string;
  hours_weekdays_end?: string;
  hours_saturday_start?: string;
  hours_saturday_end?: string;
  hours_sunday_start?: string;
  hours_sunday_end?: string;
}

const GymProfile: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const { user, updateUser } = useAuth();
  const { addToast } = useToast();

  const [formatedPhone, setFormatedPhone] = useState('');
  const [formatedCnpj, setFormatedCnpj] = useState('');
  const [formatedZipcode, setFormatedZipcode] = useState('');

  // Cobrança não é campo do form principal (unform) de propósito -- é
  // outro endpoint (dues/gym/settings), com sua própria validação. Fica
  // como estado à parte e entra no MESMO handleSubmit/botão só pra dar
  // a sensação de "um formulário só" pro usuário (pedido do Pedro,
  // 2026-08-22).
  const hasBillingPermission = !!user?.permissions?.includes(
    'manage_student_billing',
  );
  const hasBillingFeature = isFeatureGranted(user?.features?.student_billing);
  const canEditBilling = hasBillingPermission && hasBillingFeature;

  const [chargingEnabled, setChargingEnabled] = useState(true);
  const [reminderDaysBefore, setReminderDaysBefore] = useState('3');
  const [showFinancialReport, setShowFinancialReport] = useState(true);

  useEffect(() => {
    if (!canEditBilling) return;

    api
      .get('dues/gym/settings')
      .then(response => {
        setChargingEnabled(response.data.charging_enabled);
        setReminderDaysBefore(String(response.data.reminder_days_before));
        setShowFinancialReport(response.data.show_financial_report_in_reports);
      })
      .catch(() => undefined);
  }, [canEditBilling]);

  // Mesmo padrão do bloco de Cobrança acima: endpoint próprio
  // (trainings/gym/settings), fora do form principal (unform), entrando
  // no mesmo handleSubmit/botão só pra parecer "um formulário só".
  const hasTrainingManagePermission =
    !!user?.permissions?.includes('manage_trainings');

  const [defaultSetsMin, setDefaultSetsMin] = useState('3');
  const [defaultSetsMax, setDefaultSetsMax] = useState('4');
  const [defaultRepsMin, setDefaultRepsMin] = useState('8');
  const [defaultRepsMax, setDefaultRepsMax] = useState('12');
  const [defaultIntervalMin, setDefaultIntervalMin] = useState('60');

  useEffect(() => {
    if (!hasTrainingManagePermission) return;

    api
      .get('trainings/gym/settings')
      .then(response => {
        setDefaultSetsMin(String(response.data.default_sets_min));
        setDefaultSetsMax(String(response.data.default_sets_max));
        setDefaultRepsMin(String(response.data.default_reps_min));
        setDefaultRepsMax(String(response.data.default_reps_max));
        setDefaultIntervalMin(String(response.data.default_interval_min));
      })
      .catch(() => undefined);
  }, [hasTrainingManagePermission]);

  const handleSubmit = useCallback(
    async (data: FormProps): Promise<void> => {
      try {
        formRef.current?.setErrors({});

        const schema = yup.object().shape({
          name: yup.string().required('Compo obrigatório'),
          address: yup.string(),
          phone: yup
            .string()
            .nullable()
            .transform((value: string, o: string) => (o === '' ? null : value))
            .min(10, 'Insira o seu telefone com DDD'),
          cnpj: yup
            .string()
            .nullable()
            .transform((value: string, o: string) => (o === '' ? null : value))
            .transform((value: string) =>
              value ? value.replace(/\D/g, '') : value,
            )
            .length(14, 'CNPJ inválido'),
          zipcode: yup
            .string()
            .nullable()
            .transform((value: string, o: string) => (o === '' ? null : value))
            .transform((value: string) =>
              value ? value.replace(/\D/g, '') : value,
            )
            .length(8, 'CEP inválido'),
        });

        await schema.validate(data, { abortEarly: false });

        let parsedReminderDays: number | undefined;

        if (canEditBilling) {
          parsedReminderDays = Number(reminderDaysBefore);

          if (
            Number.isNaN(parsedReminderDays) ||
            parsedReminderDays < 0 ||
            parsedReminderDays > 28
          ) {
            addToast({
              type: 'error',
              title:
                'Dias de antecedência do lembrete deve estar entre 0 e 28.',
            });
            return;
          }
        }

        await api.put('gyms', {
          name: data.name,
          address: data.address,
          phone: phoneMask(data?.phone, true),
          cnpj: cnpjMask(data?.cnpj, true) || undefined,
          instagram: data.instagram,
          address_number: data.number,
          address_zipcode: cepMask(data?.zipcode, true) || undefined,
          address_city: data.city,
          address_uf: data.uf,
          capacity: data.capacity ? Number(data.capacity) : undefined,
          opening_hours: {
            weekdays: {
              open: data.hours_weekdays_start || null,
              close: data.hours_weekdays_end || null,
            },
            saturday: {
              open: data.hours_saturday_start || null,
              close: data.hours_saturday_end || null,
            },
            sunday: {
              open: data.hours_sunday_start || null,
              close: data.hours_sunday_end || null,
            },
          },
        });

        if (canEditBilling && parsedReminderDays !== undefined) {
          await api.put('dues/gym/settings', {
            charging_enabled: chargingEnabled,
            reminder_days_before: parsedReminderDays,
            show_financial_report_in_reports: showFinancialReport,
          });
        }

        if (hasTrainingManagePermission) {
          const parsedDefaults = {
            default_sets_min: Number(defaultSetsMin),
            default_sets_max: Number(defaultSetsMax),
            default_reps_min: Number(defaultRepsMin),
            default_reps_max: Number(defaultRepsMax),
            default_interval_min: Number(defaultIntervalMin),
          };

          // Servidor exige > 0 pra séries/repetições (Joi `.positive()`)
          // e >= 0 só pro descanso (Joi `.min(0)`) -- checar tudo com o
          // mesmo `< 0` deixava passar um "0" digitado nas séries/
          // repetições, que o client aceitava e o servidor rejeitava
          // (400 sem destaque de campo nenhum, já que esses campos não
          // são registrados no unform).
          const { default_interval_min: intervalValue, ...positiveDefaults } =
            parsedDefaults;

          const hasInvalidPositive = Object.values(positiveDefaults).some(
            value => Number.isNaN(value) || value <= 0,
          );
          const hasInvalidInterval =
            Number.isNaN(intervalValue) || intervalValue < 0;

          if (hasInvalidPositive || hasInvalidInterval) {
            addToast({
              type: 'error',
              title: 'Valores padrão da ficha de treino inválidos.',
            });
            return;
          }

          await api.put('trainings/gym/settings', parsedDefaults);
        }

        const userData = await api.get('users');
        updateUser(userData.data);

        addToast({
          type: 'success',
          title: 'Dados do perfil atualizados com sucesso',
        });
      } catch (err: any) {
        if (err instanceof yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          return;
        }

        addToast({
          type: 'error',
          title: 'Erro na atualização dos dados',
          description:
            'Houve um erro na atualizaçãp dos dados. Tente novamente mais tarde.',
        });
      }
    },
    [
      addToast,
      canEditBilling,
      chargingEnabled,
      reminderDaysBefore,
      showFinancialReport,
      hasTrainingManagePermission,
      defaultSetsMin,
      defaultSetsMax,
      defaultRepsMin,
      defaultRepsMax,
      defaultIntervalMin,
    ],
  );

  useEffect(() => {
    if (user?.gym?.phone) {
      setFormatedPhone(phoneMask(user.gym.phone));
    }
    if (user?.gym?.cnpj) {
      setFormatedCnpj(cnpjMask(user.gym.cnpj));
    }
    if (user?.gym?.address_zipcode) {
      setFormatedZipcode(cepMask(user.gym.address_zipcode));
    }
  }, [user]);

  return (
    <Painel>
      <Container>
        <PageTitle>Dados da academia</PageTitle>

        <Form
          ref={formRef}
          initialData={{
            name: user?.gym?.name,
            address: user?.gym?.address,
            instagram: user?.gym?.instagram,
            number: user?.gym?.address_number,
            city: user?.gym?.address_city,
            capacity: user?.gym?.capacity,
            uf: user?.gym?.address_uf
              ? { value: user.gym.address_uf, label: user.gym.address_uf }
              : undefined,
            hours_weekdays_start: user?.gym?.opening_hours?.weekdays?.open,
            hours_weekdays_end: user?.gym?.opening_hours?.weekdays?.close,
            hours_saturday_start: user?.gym?.opening_hours?.saturday?.open,
            hours_saturday_end: user?.gym?.opening_hours?.saturday?.close,
            hours_sunday_start: user?.gym?.opening_hours?.sunday?.open,
            hours_sunday_end: user?.gym?.opening_hours?.sunday?.close,
          }}
          onSubmit={handleSubmit}
        >
          <Grid>
            <div>
              <Card>
                <Separate label="Identidade visual" />
                <InputFileMedia
                  name="logo"
                  endpoint="gyms/logo"
                  formFieldName="logo"
                  identificator="gym-logo"
                  mediaUrlKey="logo_url"
                  refetchUserAfterChange
                  defaultFile={user?.gym?.logo_url}
                />
                <Info text="Imagem de até 5mb." />
              </Card>

              {/* <Card>
                <ToggleRow>
                  <div>
                    <Separate label="Visível publicamente" />
                    <p>Exibir a academia na busca do aplicativo.</p>
                  </div>
                  <DisabledField>
                    <InputCheckbox name="is_public" />
                  </DisabledField>
                </ToggleRow>
                <ComingSoonTag />
              </Card> */}

              <Card>
                <Separate label="Horário de funcionamento" />
                <Info text="Deixe os dois campos em branco pra marcar o dia como fechado." />
                <ScheduleRow>
                  <span>Seg – Sex</span>
                  <InputText name="hours_weekdays_start" placeholder="06:00" />
                  <span>–</span>
                  <InputText name="hours_weekdays_end" placeholder="22:00" />
                </ScheduleRow>
                <ScheduleRow>
                  <span>Sábado</span>
                  <InputText name="hours_saturday_start" placeholder="08:00" />
                  <span>–</span>
                  <InputText name="hours_saturday_end" placeholder="14:00" />
                </ScheduleRow>
                <ScheduleRow>
                  <span>Domingo</span>
                  <InputText name="hours_sunday_start" placeholder="Fechado" />
                  <span>–</span>
                  <InputText name="hours_sunday_end" placeholder="Fechado" />
                </ScheduleRow>
              </Card>
            </div>

            <div>
              <Card>
                <Separate label="Informações gerais" />
                <InputText name="name" label="Nome" />
                <InputText name="address" label="Endereço" />

                <InputText
                  name="phone"
                  label="Telefone"
                  type="phone"
                  maxLength={11}
                  defaultValue={formatedPhone}
                  onBlur={() => {
                    formRef.current?.setFieldValue(
                      'phone',
                      phoneMask(formRef.current?.getFieldValue('phone')),
                    );
                  }}
                  onChange={() => {
                    formRef.current?.setFieldValue(
                      'phone',
                      formRef.current
                        ?.getFieldValue('phone')
                        .replace(/\D/g, ''),
                    );
                  }}
                />
              </Card>

              <Card>
                <Separate label="Detalhes adicionais" />
                <Row2>
                  <InputText
                    name="cnpj"
                    label="CNPJ"
                    placeholder="00.000.000/0000-00"
                    maxLength={18}
                    defaultValue={formatedCnpj}
                    onBlur={() => {
                      formRef.current?.setFieldValue(
                        'cnpj',
                        cnpjMask(formRef.current?.getFieldValue('cnpj')),
                      );
                    }}
                    onChange={() => {
                      formRef.current?.setFieldValue(
                        'cnpj',
                        formRef.current
                          ?.getFieldValue('cnpj')
                          .replace(/\D/g, ''),
                      );
                    }}
                  />
                  <InputText
                    name="instagram"
                    label="Instagram"
                    placeholder="@minhaacademia"
                  />
                </Row2>
                <Row4>
                  <InputText name="number" label="Número" placeholder="123" />
                  <InputText
                    name="zipcode"
                    label="CEP"
                    placeholder="59000-000"
                    maxLength={9}
                    defaultValue={formatedZipcode}
                    onBlur={() => {
                      formRef.current?.setFieldValue(
                        'zipcode',
                        cepMask(formRef.current?.getFieldValue('zipcode')),
                      );
                    }}
                    onChange={() => {
                      formRef.current?.setFieldValue(
                        'zipcode',
                        formRef.current
                          ?.getFieldValue('zipcode')
                          .replace(/\D/g, ''),
                      );
                    }}
                  />
                  <InputText name="city" label="Cidade" placeholder="Natal" />
                  <Select
                    key={user?.gym?.address_uf || 'uf-empty'}
                    name="uf"
                    label="UF"
                    options={UF_OPTIONS}
                  />
                </Row4>
                <InputText
                  name="capacity"
                  label="Capacidade máxima"
                  type="number"
                  placeholder="220"
                />
              </Card>
            </div>

            {hasBillingPermission && (
              <FullWidthCard>
                <Separate label="Cobrança" />

                <FeatureGate requiredFeature="student_billing">
                  <ToggleRow>
                    <div>
                      <Separate label="Cobrança de mensalidades" />
                      <p>
                        Desativar interrompe os lembretes de vencimento e o
                        alerta de inadimplência, sem apagar os cadastros e
                        pagamentos já registrados.
                      </p>
                    </div>
                    <ToggleTrack
                      type="button"
                      isChecked={chargingEnabled}
                      onClick={() => setChargingEnabled(state => !state)}
                    />
                  </ToggleRow>

                  <Field style={{ maxWidth: 220 }}>
                    <label htmlFor="reminder_days_before">
                      Dias de antecedência do lembrete
                    </label>
                    <FieldInput
                      id="reminder_days_before"
                      type="number"
                      value={reminderDaysBefore}
                      onChange={e => setReminderDaysBefore(e.target.value)}
                      disabled={!chargingEnabled}
                    />
                    <p>
                      A notificação é enviada uma vez, nesse número de dias
                      antes do vencimento da mensalidade.
                    </p>
                  </Field>

                  <ToggleRow>
                    <div>
                      <Separate label="Módulo financeiro em Relatórios" />
                      <p>
                        Mostra a quantidade de inadimplentes e o saldo de
                        mensalidades recebidas no mês na tela de Relatórios.
                      </p>
                    </div>
                    <ToggleTrack
                      type="button"
                      isChecked={showFinancialReport}
                      onClick={() => setShowFinancialReport(state => !state)}
                    />
                  </ToggleRow>
                </FeatureGate>
              </FullWidthCard>
            )}

            {hasTrainingManagePermission && (
              <FullWidthCard>
                <Separate label="Ficha de treino" />
                <Info text="Valores usados ao adicionar um exercício rápido, sem configurar antes. Continuam editáveis exercício a exercício na atribuição." />

                <Row5>
                  <Field>
                    <label htmlFor="default_sets_min">Séries (mín.)</label>
                    <FieldInput
                      id="default_sets_min"
                      type="number"
                      value={defaultSetsMin}
                      onChange={e => setDefaultSetsMin(e.target.value)}
                    />
                  </Field>
                  <Field>
                    <label htmlFor="default_sets_max">Séries (máx.)</label>
                    <FieldInput
                      id="default_sets_max"
                      type="number"
                      value={defaultSetsMax}
                      onChange={e => setDefaultSetsMax(e.target.value)}
                    />
                  </Field>
                  <Field>
                    <label htmlFor="default_reps_min">Repetições (mín.)</label>
                    <FieldInput
                      id="default_reps_min"
                      type="number"
                      value={defaultRepsMin}
                      onChange={e => setDefaultRepsMin(e.target.value)}
                    />
                  </Field>
                  <Field>
                    <label htmlFor="default_reps_max">Repetições (máx.)</label>
                    <FieldInput
                      id="default_reps_max"
                      type="number"
                      value={defaultRepsMax}
                      onChange={e => setDefaultRepsMax(e.target.value)}
                    />
                  </Field>
                  <Field>
                    <label htmlFor="default_interval_min">
                      Descanso (seg.)
                    </label>
                    <FieldInput
                      id="default_interval_min"
                      type="number"
                      value={defaultIntervalMin}
                      onChange={e => setDefaultIntervalMin(e.target.value)}
                    />
                  </Field>
                </Row5>
              </FullWidthCard>
            )}

            <Confirm name="submit" type="submit" value="Confirmar alterações" />
          </Grid>
        </Form>
      </Container>
    </Painel>
  );
};

export { GymProfile };
